import { atom, selectorFamily, selector } from 'recoil'
import { default as _set } from 'lodash/set'
import { default as _get } from 'lodash/get'
import { default as _pick } from 'lodash/pick'
import { default as _remove } from 'lodash/remove'
import { produce } from 'immer'
import {
  createOrderProduct,
  getOrderProducts,
  updateOrderProduct,
} from '@/api/orderProducts'
import { updateOrder } from '@/api/orders'
import { orderProductsSync } from '@/utils/global/orderProductsSync'
import { loggerSentry } from '@/utils/loggerSentry'
import { applicationTypeAtom } from './globalAtoms'

// Counter to discard stale getOrderProducts responses during rapid product adds
let productRefreshId = 0

export const selectedProductsAtom = atom({
  key: 'selectedProducts',
  default: [],
})

export const selectedThemeAtom = atom({
  key: 'selectedTheme',
  default: null,
})

export const themesArrayAtom = atom({
  key: 'themesArray',
  default: [],
})

export const currentProductIdAtom = atom({
  key: 'currentProductId',
  default: null,
})

export const mappedProductsAtom = atom({
  key: 'mappedProducts',
  default: [],
})

export const pinnedThemesAtom = atom({
  key: 'pinnedThemes',
  default: [],
})

export const customizationAtom = atom({
  key: 'customization',
  default: null,
})

export const orderAtom = atom({
  key: 'currentOrder',
  default: {},
})

export const orderIdAtom = atom({
  key: 'currentOrderId',
  default: null,
})

//the order selector will let us work with the individual properties on the order atom which is an object
export const orderSelector = selectorFamily({
  key: 'orderSelector',
  get:
    ({ property }) =>
    ({ get }) => {
      const order = get(orderAtom)
      if (!order.id) return null
      const value = _get(order, property)
      //if we request the 'theme_id' the value is all that will be returned, in this case, a uuid
      return value
    },
  set:
    ({ property }) =>
    ({ set, get }, newValue) => {
      const order = get(orderAtom)
      if (!order) return null
      //here we can target an update only the property being edited
      const newField = produce(order, (draft) => {
        _set(draft, property, newValue)
      })
      //this will allow us to send that one update to the backend at the same time
      const payload = _pick(newField, property)
      updateOrder(newField.id, payload)
      //then we set the order atom with the new value
      set(orderAtom, newField)
    },
})

export const themesAtom = atom({
  key: 'themes',
  default: [],
})

export const customThemesAtom = atom({
  key: 'customThemes',
  default: [],
})

export const layoutAtom = atom({
  key: 'layout',
  default: '',
})

export const pinnedThemesSelector = selector({
  key: 'pinnedThemeSelector',
  get: ({ get }) => {
    const pinnedThemes = get(pinnedThemesAtom)
    if (!pinnedThemes) return null
    return pinnedThemes
  },
  set: ({ set, get }, newValue) => {
    const pinnedThemes = get(pinnedThemesAtom)
    if (!pinnedThemes) return null
    //determine if the clicked theme is in the pinned themes array
    const index = pinnedThemes.findIndex(
      (item) => item.id === newValue.id || item.theme_id === newValue.id,
    )
    const pinnedTheme = pinnedThemes.find(
      (item) => item.id === newValue.id || item.theme_id === newValue.id,
    )

    let newField
    if (index < 0) {
      //if it is NOT in the array (index === -1) add it to the atom array
      newField = produce(pinnedThemes, (draft) => {
        draft.push(newValue)
      })
    } else {
      //if it IS in the array remove it from the array
      newField = produce(pinnedThemes, (draft) => {
        _remove(draft, pinnedTheme)
      })
    }
    set(pinnedThemesAtom, newField)
  },
})

export const selectedThemeSelector = selector({
  key: 'selectedThemeSelector',
  get: ({ get }) => {
    const selectedTheme = get(selectedThemeAtom)
    if (!selectedTheme) return null
    return selectedTheme
  },
  set: ({ set, get }, newValue) => {
    const order = get(orderAtom)
    if (!newValue) return null
    if (!order?.id) {
      loggerSentry.error({
        message: 'Theme selector used before order was hydrated',
        category: 'theme-selector',
      })
      return null
    }
    //for a pinned theme the theme id is `theme_id`, for normal themes the id is `id`
    const themeId = newValue?.theme_id || newValue.id
    const appType = get(applicationTypeAtom)
    // Single call: updates order theme, propagates to all products,
    // and clears their page sets. The editor/preview recreates page
    // sets with the new theme on load.
    // Same stale-response counter the product-add flow uses: on rapid theme
    // switches only the LAST refetch broadcasts, so an earlier theme's
    // products can never overwrite a later one's.
    const thisRefreshId = ++productRefreshId
    updateOrder(order.id, { theme_id: themeId })
      .then(async () => {
        // Re-fetch products so subscribers see the NEW per-product theme_ids.
        // A bare notify() leaves mappedProducts stale, and the lazy page-set
        // rebuild then pulls templates for the OLD theme (remnants bug).
        const { data } = await getOrderProducts(
          order.id,
          order.case_id,
          appType,
        )
        if (thisRefreshId === productRefreshId) {
          orderProductsSync.notify({ products: data })
        }
      })
      .catch((err) => {
        loggerSentry.error({
          message: 'Failed to update order theme',
          category: 'theme-selector',
          error: err,
        })
        orderProductsSync.notify()
      })
    set(selectedThemeAtom, newValue)
  },
})

// Selector to get the current theme for M3 based on selected product
export const currentProductThemeSelector = selector({
  key: 'currentProductThemeSelector',
  get: ({ get }) => {
    const themesArray = get(themesArrayAtom)
    const currentProductId = get(currentProductIdAtom)
    const mappedProducts = get(mappedProductsAtom)

    // If no themes available, return null
    if (themesArray.length === 0) {
      return null
    }

    // If we don't have product data yet, return the first theme as fallback
    if (!mappedProducts.length || !currentProductId) {
      return themesArray[0] || null
    }

    // Find the product in the mapped products to get its theme_id
    const product = mappedProducts.find((p) => p.id === currentProductId)

    if (!product || !product.theme_id) {
      // Fallback to first theme if product not found or no theme
      return themesArray[0] || null
    }

    // Find the theme in the themes array
    const foundTheme = themesArray.find(
      (theme) => theme.id === product.theme_id,
    )
    return foundTheme || themesArray[0] || null
  },
})

// Universal theme selector that works for both MDL and M3
export const universalThemeSelector = selector({
  key: 'universalThemeSelector',
  get: ({ get }) => {
    const selectedTheme = get(selectedThemeAtom)
    const themesArray = get(themesArrayAtom)

    // Both M3 and MDL now support per-product themes
    const productTheme = get(currentProductThemeSelector)

    if (productTheme) {
      return productTheme
    } else if (selectedTheme) {
      return selectedTheme
    } else if (themesArray.length > 0) {
      return themesArray[0]
    }
    return null
  },
})

export const productsAtom = atom({
  key: 'products',
  default: [],
})

// Cache for useIsM3Order's on-demand lookup (editor / dashboard contexts
// where the package-flow atoms above are never populated). `orderId` keys the
// cached verdict so switching orders re-checks.
export const orderM3ProductsCheckAtom = atom({
  key: 'orderM3ProductsCheck',
  default: { orderId: null, hasM3Products: false },
})

export const selectedProductsSelector = selector({
  key: 'selectedProductsSelector',
  get: ({ get }) => {
    const selectedProducts = get(selectedProductsAtom)
    if (!selectedProducts) return null
    return selectedProducts
  },

  set: ({ set, get }, newValue) => {
    const selectedProducts = get(selectedProductsAtom)
    const order = get(orderAtom)
    const products = get(productsAtom)
    if (!selectedProducts) return null
    const orderProductExists = selectedProducts.find(
      (item) => item.product_id === newValue.id || item.id === newValue.id,
    )
    const index = selectedProducts.findIndex(
      (item) => item.product_id === newValue.id || item.id === newValue.id,
    )
    let newField
    if (!orderProductExists) {
      //if the product has not already been added to the order we will create a new one
      //select only the values we want from the product
      const payload = _pick(newValue, ['name', 'thumbnail'])
      //add values we need for the orderProduct
      payload.product_id = newValue.id
      payload.order_id = order.id
      payload.selected = true
      //assign the order theme if no product-level theme exists
      if (order.theme_id) {
        payload.theme_id = order.theme_id
      }
      //send the new orderProduct to the database (we cannot await in the set function)
      //after creation, re-fetch all products from DB so the atom has real IDs
      //this prevents the theme setter from skipping products that lack an id
      const appType = get(applicationTypeAtom)
      const thisRefreshId = ++productRefreshId
      createOrderProduct(payload)
        .then(async () => {
          try {
            //if we are adding a Register Book we also need to create the cover page
            //before re-fetching, so both products exist in the DB
            if (
              payload.name === 'Register Book' ||
              payload.name === 'Deluxe Register Book'
            ) {
              const coverPage = products.find(
                (item) => item.name === `${payload.name}: Front Insert`,
              )
              if (coverPage) {
                const coverPagePayload = _pick(coverPage, ['name', 'thumbnail'])
                coverPagePayload.product_id = coverPage.id
                coverPagePayload.order_id = order.id
                coverPagePayload.selected = payload.selected
                if (order.theme_id) {
                  coverPagePayload.theme_id = order.theme_id
                }
                await createOrderProduct(coverPagePayload)
              }
            }
            const { data } = await getOrderProducts(
              order.id,
              order.case_id,
              appType,
            )
            // Pass re-fetched products through the sync mechanism instead of
            // calling set() async (Recoil does not support async selector sets)
            if (thisRefreshId === productRefreshId) {
              orderProductsSync.notify({ products: data })
            }
          } catch (err) {
            loggerSentry.error({
              message: 'Failed to create/re-fetch order products',
              category: 'product-selector',
              error: err,
            })
            orderProductsSync.notify()
          }
        })
        .catch((err) => {
          loggerSentry.error({
            message: 'Failed to create order product',
            category: 'product-selector',
            error: err,
          })
          orderProductsSync.notify()
        })
      //add the new orderProduct to the selectedProductsAtom array
      newField = produce(selectedProducts, (draft) => {
        draft.push(payload)
      })
    } else {
      //if the orderProduct is already in the selectedProducts array we need to update it instead
      //we toggle the 'selected' property true/false
      const payload = { selected: !orderProductExists.selected }
      //since the set cannot run an async operation, we need to refresh the selectedProducts array which happends on the card selector, this will make sure that all elements in the array have an id (are fresh from the db) before we get to this step. see cardSelector selectedStateUpdate
      //send the updated property only to the db
      //notify when the operation completes so themes can be refreshed
      updateOrderProduct(orderProductExists.id, payload).then(() =>
        orderProductsSync.notify(),
      )
      //update the item in the selectedProducts array by targeting it at the index
      newField = produce(selectedProducts, (draft) => {
        _set(draft, `[${index}].selected`, !orderProductExists.selected)
      })
      //if the selected product is a Reg Book/Deluxe RB we also need to toggle selected for the front insert
      if (
        orderProductExists.name === 'Register Book' ||
        orderProductExists.name === 'Deluxe Register Book'
      ) {
        const coverPage = selectedProducts.find(
          (item) => item.name === `${orderProductExists.name}: Front Insert`,
        )
        const coverPageIndex = selectedProducts.findIndex(
          (item) => item.name === `${orderProductExists.name}: Front Insert`,
        )
        if (coverPage) {
          const coverPagePayload = { selected: payload.selected }

          updateOrderProduct(coverPage.id, coverPagePayload).then(() =>
            orderProductsSync.notify(),
          )
          newField = produce(newField, (draft) => {
            _set(draft, `[${coverPageIndex}].selected`, payload.selected)
          })
        }
      }
    }
    //update the selected Products array
    set(selectedProductsAtom, newField)
  },
})

//this is cool but not useful at the moment
export const asyncSelectedProductsSelector = selector({
  key: 'asyncSelectedProducts',
  get: async ({ get }) => {
    const order = get(orderAtom)
    if (!order.id) {
      return []
    } else {
      const { data } = await getOrderProducts(order?.id)
      return data
    }
  },
})
